import React, { memo, useCallback, useMemo } from "react";
import { gazetteerState } from "@mirage/store/gazetteerStore";
import { useRecoilValue } from "recoil";
import { GazetteerRecordParsed, GazetteerService } from "@shared/mirage/services/GazetteerService";
import { AddressHelper } from "@shared/mirage/helpers/AddressHelper";
import { GovField } from "./GovField";

interface GovAddressBoxProps {
    onSelect?: ({ address, addressDetails, point, addressLines }: ISelectedAddressRecord) => void;
}

export const GovAddressBox = memo((props: GovAddressBoxProps) => {
    const { onSelect } = props;
    const gazetteerSettings = useRecoilValue(gazetteerState);
    const localGazetteerToken = gazetteerSettings.localGazetteerToken;
    const localGazetteerUrl = gazetteerSettings.localGazetteerUrl;
    const localGazetteerService = useMemo(() => new GazetteerService(localGazetteerUrl, localGazetteerToken), [localGazetteerToken, localGazetteerUrl]);
    const getAddressBySelection = useCallback(
        async (value: AddressBoxOption | null) => {
            if (typeof value === "string" || !value?.id) {
                return;
            }
            const data = await localGazetteerService.getAddressById(value.id);
            if (data?.record) {
                const record: GazetteerRecordParsed = JSON.parse(data.record);
                const point = { easting: parseInt(data.point.easting), northing: parseInt(data.point.northing) };
                const addressLines = AddressHelper.getAddressLinesFromGazetteerRecord(record);
                onSelect && onSelect({ address: value.label, addressDetails: record, point, addressLines });
            }
        },
        [onSelect, localGazetteerService]
    );
    const optionOnSelect = useCallback(
        async (selectedAddress: string | AddressBoxOption | null) => {
            if (!selectedAddress || typeof selectedAddress === "string") {
                return;
            }
            await getAddressBySelection(selectedAddress);
        },
        [getAddressBySelection]
    );

    const getAddresses = useCallback(
        async (inputVal: string | null) => {
            if (inputVal && inputVal.length >= 2) {
                const addresses = await localGazetteerService.getGazetteerAddresses(inputVal);
                const options = addresses?.map((opt) => ({ label: opt.label, id: opt.id })) || [];
                return options;
            }
            return [];
        },
        [localGazetteerService]
    );

    const onChange = useCallback((event: React.SyntheticEvent<Element, Event>, value: string | null) => getAddresses(value as string), [getAddresses]);

    return <GovField contentType="AUTOCOMPLETE" label="Search For Address" onChange={onChange} onSelected={optionOnSelect} placeholder="Start typing to search for an address" />;
});

interface AddressBoxOption {
    label: string;
    id: string;
}
export interface ISelectedAddressRecord {
    address: string;
    addressDetails: GazetteerRecordParsed;
    point: { easting: number; northing: number };
    addressLines: { line1: string; line2: string; line3: string };
}
